import React, { useEffect, useMemo, useState, useContext } from "react";
import { ThemeContext } from "../../context/ThemeContext";
import getCurrentSkin from "../../utils/getCurrentSkin";
import HeaderBar from "../../components/HeaderBar";
import PaymentLayoutWrapper from "../../components/PaymentLayoutWrapper";
import { useNavigate } from "react-router-dom";
import axios from "axios";
import { toast, ToastContainer } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";
import {
  FaPlus,
  FaSearch,
  FaTrash,
  FaEdit,
  FaChevronUp,
  FaChevronDown,
} from "react-icons/fa";

const BASE_URL = "https://nissi-india.com";

const ContractorProjectListing = () => {
  const { isDark, skin, setSkin } = useContext(ThemeContext);
  const currentSkin = getCurrentSkin(skin);
  const { baseColor, baseShade } = currentSkin;
  const buttonClass = `bg-${baseColor}-${baseShade}`;
  const hoverClass = `hover:bg-${baseColor}-700`;
  const navigate = useNavigate();

  // -------------------- localStorage helpers --------------------
  const getLS = (key, fallback = "") => {
    if (typeof window === "undefined") return fallback;
    const val = localStorage.getItem(key);
    return val !== null && val !== undefined && val !== "" ? val : fallback;
  };

  const toStr = (val, fallback = "") => {
    if (val === null || val === undefined) return fallback;
    const str = String(val).trim();
    return str === "" ? fallback : str;
  };

  const toNum = (val, fallback = 0) => {
    const num = Number(val);
    return Number.isFinite(num) ? num : fallback;
  };

  // -------------------- localStorage values --------------------
  const finYearId = useMemo(() => toStr(getLS("finYearId")), []);
  const finYear = useMemo(() => toStr(getLS("finYear")), []);
  const branchid = useMemo(
    () => toStr(getLS("branchId") || getLS("branchid")),
    []
  );
  const branchtype = useMemo(
    () => toStr(getLS("branchType") || getLS("branchtype"), "HO"),
    []
  );
  const loginuserid = useMemo(
    () =>
      toNum(
        getLS("userId") || getLS("loginuserid") || getLS("loginUserId"),
        0
      ),
    []
  );

  // -------------------- common headers --------------------
  const commonHeaders = useMemo(
    () => ({
      "x-finyearid": finYearId,
      "x-finyear": finYear,
      "x-branchid": branchid,
      "x-branch": branchtype,
      "x-userid": loginuserid,
    }),
    [finYearId, finYear, branchid, branchtype, loginuserid]
  );

  const [showFilters, setShowFilters] = useState(true);

  // Search state
  const [searchData, setSearchData] = useState({
    contractorName: "",
    contractorId: "",
    projectName: "",
    mappingCode: "",
    contractorCode: "",
  });

  // dropdown states
  const [ddlContractorNames, setDdlContractorNames] = useState([]);
  const [ddlContractorCodes, setDdlContractorCodes] = useState([]);
  const [ddlProjects, setDdlProjects] = useState([]);
  const [ddlMappingCodes, setDdlMappingCodes] = useState([]);

  // table data
  const [projectData, setProjectData] = useState([]);
  const [loading, setLoading] = useState(false);
  const [ddlLoading, setDdlLoading] = useState(false);

  // checkbox state
  const [selectedIds, setSelectedIds] = useState([]);

  const allSelected = useMemo(() => {
    if (!projectData.length) return false;
    return selectedIds.length === projectData.length;
  }, [selectedIds, projectData]);

  // ------------------ Load Dropdowns ------------------
  const loadDropdowns = async () => {
    try {
      if (!branchid) {
        toast.error("BranchId not found in localStorage!");
        return;
      }

      setDdlLoading(true);

      const [names, codes, projects, maps] = await Promise.all([
        axios.get(`${BASE_URL}/api/contractorProject/contractors`, {
          params: { branchid },
          headers: commonHeaders,
        }),
        axios.get(`${BASE_URL}/api/contractorProject/contractorCodes`, {
          params: { branchid },
          headers: commonHeaders,
        }),
        axios.get(`${BASE_URL}/api/contractorProject/projects`, {
          params: { branchid, branchtype },
          headers: commonHeaders,
        }),
        axios.get(`${BASE_URL}/api/contractorProject/mappingCodes`, {
          params: { branchid },
          headers: commonHeaders,
        }),
      ]);

      setDdlContractorNames(Array.isArray(names.data?.data) ? names.data.data : []);
      setDdlContractorCodes(Array.isArray(codes.data?.data) ? codes.data.data : []);
      setDdlProjects(Array.isArray(projects.data?.data) ? projects.data.data : []);
      setDdlMappingCodes(Array.isArray(maps.data?.data) ? maps.data.data : []);
    } catch (err) {
      console.error("Dropdown Load Error:", err?.response?.data || err.message);
      toast.error(err?.response?.data?.message || "Failed to load dropdowns");
      setDdlContractorNames([]);
      setDdlContractorCodes([]);
      setDdlProjects([]);
      setDdlMappingCodes([]);
    } finally {
      setDdlLoading(false);
    }
  };

  useEffect(() => {
    loadDropdowns();
    handleSearch(true);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const handleSearchChange = (field, value) => {
    setSearchData((prev) => ({ ...prev, [field]: value }));
  };

  // ------------------ Search/List ------------------
  const handleSearch = async (silent = false) => {
    try {
      if (!branchid) {
        toast.error("BranchId not found in localStorage!");
        return;
      }

      setLoading(true);
      setSelectedIds([]);

      const res = await axios.get(`${BASE_URL}/api/contractorProject/list`, {
        params: {
          branchid,
          branchtype,
          contractorId: searchData.contractorId,
          contractorName: searchData.contractorName,
          projectName: searchData.projectName,
          mappingCode: searchData.mappingCode,
          contractorCode: searchData.contractorCode,
        },
        headers: commonHeaders,
      });

      setProjectData(Array.isArray(res.data?.data) ? res.data.data : []);

      if (!silent) toast.success("Search completed");
    } catch (err) {
      console.error("Search Error:", err?.response?.data || err.message);
      toast.error(err?.response?.data?.message || err.message);
      setProjectData([]);
    } finally {
      setLoading(false);
    }
  };

  // ------------------ Add New ------------------
  const handleAddNew = () => {
    navigate("/PaymentVouchers/ContractorProjectEntry");
  };

  // ------------------ Checkbox Logic ------------------
  const toggleSelectAll = () => {
    if (allSelected) {
      setSelectedIds([]);
    } else {
      setSelectedIds(
        (Array.isArray(projectData) ? projectData : []).map((x) => x.mappingId)
      );
    }
  };

  const toggleRow = (mappingId) => {
    setSelectedIds((prev) => {
      if (prev.includes(mappingId)) {
        return prev.filter((id) => id !== mappingId);
      }
      return [...prev, mappingId];
    });
  };

  // ------------------ Delete ------------------
  const deleteOne = async (mappingId) => {
    const ok = window.confirm("Are you sure you want to delete this record?");
    if (!ok) return;

    if (!loginuserid) {
      toast.error("UserId not found in localStorage!");
      return;
    }

    try {
      await axios.delete(`${BASE_URL}/api/contractorProject/delete/${mappingId}`, {
        params: { loginuserid, branchid },
        headers: commonHeaders,
      });

      toast.success("Deleted Successfully");
      handleSearch(true);
    } catch (err) {
      console.error("Delete One Error:", err?.response?.data || err.message);
      toast.error(err?.response?.data?.message || err.message);
    }
  };

  const deleteSelected = async () => {
    if (!selectedIds.length) {
      toast.warn("Please select at least one row to delete.");
      return;
    }

    if (!loginuserid) {
      toast.error("UserId not found in localStorage!");
      return;
    }

    const ok = window.confirm(
      `Are you sure you want to delete ${selectedIds.length} record(s)?`
    );
    if (!ok) return;

    try {
      await Promise.all(
        selectedIds.map((id) =>
          axios.delete(`${BASE_URL}/api/contractorProject/delete/${id}`, {
            params: { loginuserid, branchid },
            headers: commonHeaders,
          })
        )
      );

      toast.success("Deleted Selected Successfully");
      setSelectedIds([]);
      handleSearch(true);
    } catch (err) {
      console.error("Delete Selected Error:", err?.response?.data || err.message);
      toast.error(err?.response?.data?.message || err.message);
    }
  };

  // ------------------ Update ------------------
  const handleUpdate = (row) => {
    navigate("/PaymentVouchers/ContractorProjectEntry", {
      state: { editData: row },
    });
  };

  const formatAmount = (amount) => {
    const n = Number(amount ?? 0);
    return n.toFixed(2);
  };

  return (
    <PaymentLayoutWrapper>
      <div className={`min-h-screen p-6 ${isDark ? "bg-gray-900" : "bg-gray-100"}`}>
        <HeaderBar
          title={<span className="font-dmserif text-3xl">CONTRACTOR PROJECT LISTING</span>}
          setSkin={setSkin}
          currentSkin={skin}
        />

        <div
          className={`p-6 mt-6 rounded-2xl shadow-xl border ${
            isDark
              ? "bg-gradient-to-br from-white/20 to-white/5 backdrop-blur-md border-white/10 text-white"
              : "bg-white text-black"
          }`}
        >
          <div className="flex justify-between items-center mb-6">
            <h1 className={`text-2xl font-bold ${isDark ? "text-white" : "text-gray-900"}`}>
              Contractor Project Listing
            </h1>

            <div className="flex items-center gap-3">
              {ddlLoading && (
                <span className={`text-sm ${isDark ? "text-gray-300" : "text-gray-600"}`}>
                  Loading dropdowns...
                </span>
              )}

              <button
                onClick={() => setShowFilters(!showFilters)}
                className={`flex items-center gap-2 px-4 py-2 rounded-lg font-semibold shadow-lg hover:shadow-xl transform transition duration-200 ${
                  isDark
                    ? "bg-gray-700 hover:bg-gray-600 text-white border border-gray-600"
                    : "bg-gray-100 hover:bg-gray-200 text-gray-700 border border-gray-300"
                }`}
              >
                {showFilters ? <FaChevronUp /> : <FaChevronDown />}
                {showFilters ? "Hide Filters" : "Show Filters"}
              </button>
            </div>
          </div>

          {showFilters && (
            <div>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mb-6">
                <div>
                  <label className={`block text-sm font-medium mb-2 ${isDark ? "text-gray-300" : "text-gray-700"}`}>
                    Contractor Name
                  </label>
                  <select
                    value={searchData.contractorName}
                    onChange={(e) => handleSearchChange("contractorName", e.target.value)}
                    className={`w-full px-4 py-2 border rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent transition-all ${
                      isDark ? "bg-gray-700 border-gray-600 text-white" : "bg-white border-gray-300 text-gray-900"
                    }`}
                  >
                    <option value="">Select</option>
                    {ddlContractorNames.map((x, index) => (
                      <option key={`${x.value || index}-${x.label || index}`} value={x.label}>
                        {x.label}
                      </option>
                    ))}
                  </select>
                </div>

                <div>
                  <label className={`block text-sm font-medium mb-2 ${isDark ? "text-gray-300" : "text-gray-700"}`}>
                    Contractor Code
                  </label>
                  <select
                    value={searchData.contractorCode}
                    onChange={(e) => handleSearchChange("contractorCode", e.target.value)}
                    className={`w-full px-4 py-2 border rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent transition-all ${
                      isDark ? "bg-gray-700 border-gray-600 text-white" : "bg-white border-gray-300 text-gray-900"
                    }`}
                  >
                    <option value="">Select</option>
                    {ddlContractorCodes.map((x, index) => (
                      <option key={`${x.value || index}-${x.label || index}`} value={x.label}>
                        {x.label}
                      </option>
                    ))}
                  </select>
                </div>

                <div>
                  <label className={`block text-sm font-medium mb-2 ${isDark ? "text-gray-300" : "text-gray-700"}`}>
                    Project Name
                  </label>
                  <select
                    value={searchData.projectName}
                    onChange={(e) => handleSearchChange("projectName", e.target.value)}
                    className={`w-full px-4 py-2 border rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent transition-all ${
                      isDark ? "bg-gray-700 border-gray-600 text-white" : "bg-white border-gray-300 text-gray-900"
                    }`}
                  >
                    <option value="">Select</option>
                    {ddlProjects.map((x, index) => (
                      <option key={`${x.value || index}-${x.label || index}`} value={x.label}>
                        {x.label}
                      </option>
                    ))}
                  </select>
                </div>

                <div>
                  <label className={`block text-sm font-medium mb-2 ${isDark ? "text-gray-300" : "text-gray-700"}`}>
                    Mapping Code
                  </label>
                  <select
                    value={searchData.mappingCode}
                    onChange={(e) => handleSearchChange("mappingCode", e.target.value)}
                    className={`w-full px-4 py-2 border rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent transition-all ${
                      isDark ? "bg-gray-700 border-gray-600 text-white" : "bg-white border-gray-300 text-gray-900"
                    }`}
                  >
                    <option value="">Select</option>
                    {ddlMappingCodes.map((x, index) => (
                      <option key={`${x.value || index}-${x.label || index}`} value={x.label}>
                        {x.label}
                      </option>
                    ))}
                  </select>
                </div>
              </div>

              <div className="flex justify-center items-center gap-6 mb-6">
                <button
                  onClick={handleAddNew}
                  className={`flex items-center gap-2 px-6 py-2 rounded-lg font-semibold shadow-lg hover:shadow-xl transform hover:-translate-y-0.5 transition-all duration-200 ${buttonClass} ${hoverClass} text-white`}
                >
                  <FaPlus /> Add New
                </button>

                <button
                  onClick={() => handleSearch(false)}
                  className={`flex items-center gap-2 px-6 py-2 rounded-lg font-semibold shadow-lg hover:shadow-xl transform hover:-translate-y-0.5 transition-all duration-200 ${buttonClass} ${hoverClass} text-white`}
                >
                  <FaSearch /> Search
                </button>
              </div>
            </div>
          )}

          <div className={`rounded-xl shadow-sm border overflow-hidden ${isDark ? "border-gray-700" : "border-gray-200"}`}>
            <div className="overflow-x-auto">
              <table className="w-full text-xs">
                <thead className={`${buttonClass} text-white`}>
                  <tr>
                    <th className="px-3 py-3 text-center font-semibold border-r border-white/20">
                      <input type="checkbox" checked={allSelected} onChange={toggleSelectAll} />
                    </th>
                    <th className="px-3 py-3 text-center font-semibold border-r border-white/20">Sl.No</th>
                    <th className="px-3 py-3 text-center font-semibold border-r border-white/20">Mapping Code</th>
                    <th className="px-3 py-3 text-center font-semibold border-r border-white/20">Contractor Id</th>
                    <th className="px-3 py-3 text-center font-semibold border-r border-white/20">Contractor Code</th>
                    <th className="px-3 py-3 text-center font-semibold border-r border-white/20">Contractor Name</th>
                    <th className="px-3 py-3 text-center font-semibold border-r border-white/20">Work Type Name</th>
                    <th className="px-3 py-3 text-center font-semibold border-r border-white/20">Project Name</th>
                    <th className="px-3 py-3 text-center font-semibold border-r border-white/20">Amount</th>
                    <th className="px-3 py-3 text-center font-semibold">Actions</th>
                  </tr>
                </thead>

                <tbody className="divide-y divide-gray-200 dark:divide-gray-700">
                  {loading ? (
                    <tr>
                      <td colSpan={10} className="px-3 py-6 text-center">
                        Loading...
                      </td>
                    </tr>
                  ) : projectData.length === 0 ? (
                    <tr>
                      <td colSpan={10} className="px-3 py-6 text-center">
                        No records found
                      </td>
                    </tr>
                  ) : (
                    projectData.map((p, index) => (
                      <tr
                        key={p.mappingId || index}
                        className={`transition-colors duration-200 ${
                          isDark ? "bg-gray-800 hover:bg-gray-750" : "bg-white hover:bg-gray-50"
                        }`}
                      >
                        <td className={`px-3 py-2 text-center border-r ${isDark ? "border-gray-700" : "border-gray-200"}`}>
                          <input
                            type="checkbox"
                            checked={selectedIds.includes(p.mappingId)}
                            onChange={() => toggleRow(p.mappingId)}
                          />
                        </td>

                        <td className={`px-3 py-2 text-center border-r ${isDark ? "border-gray-700 text-white" : "border-gray-200 text-gray-900"}`}>
                          {p.slNo}
                        </td>

                        <td className={`px-3 py-2 text-center border-r ${isDark ? "border-gray-700 text-blue-400" : "border-gray-200 text-blue-600"}`}>
                          {p.mappingCode}
                        </td>

                        <td className={`px-3 py-2 text-center border-r ${isDark ? "border-gray-700 text-white" : "border-gray-200 text-gray-900"}`}>
                          {p.contractorId}
                        </td>

                        <td className={`px-3 py-2 text-center border-r ${isDark ? "border-gray-700 text-white" : "border-gray-200 text-gray-900"}`}>
                          {p.contractorCode}
                        </td>

                        <td className={`px-3 py-2 text-center border-r ${isDark ? "border-gray-700 text-white" : "border-gray-200 text-gray-900"}`}>
                          {p.contractorName}
                        </td>

                        <td className={`px-3 py-2 text-left border-r ${isDark ? "border-gray-700 text-white" : "border-gray-200 text-gray-900"}`}>
                          {p.workTypeName}
                        </td>

                        <td className={`px-3 py-2 text-left border-r ${isDark ? "border-gray-700 text-white" : "border-gray-200 text-gray-900"}`}>
                          {p.projectName}
                        </td>

                        <td className={`px-3 py-2 text-right font-mono border-r ${isDark ? "border-gray-700 text-green-400" : "border-gray-200 text-green-600"}`}>
                          {formatAmount(p.amount)}
                        </td>

                        <td className="px-3 py-2 text-center">
                          <div className="flex justify-center gap-2">
                            <button
                              onClick={() => handleUpdate(p)}
                              className="px-3 py-1 rounded bg-yellow-500 text-white hover:bg-yellow-600 flex items-center gap-1"
                            >
                              <FaEdit /> Update
                            </button>

                            <button
                              onClick={() => deleteOne(p.mappingId)}
                              className="px-3 py-1 rounded bg-red-600 text-white hover:bg-red-700 flex items-center gap-1"
                            >
                              <FaTrash /> Delete
                            </button>
                          </div>
                        </td>
                      </tr>
                    ))
                  )}
                </tbody>
              </table>
            </div>
          </div>

          <div className="flex flex-wrap justify-center gap-4 mt-6">
            <button
              onClick={handleAddNew}
              className={`flex items-center gap-2 px-6 py-2 rounded-lg font-semibold shadow-lg hover:shadow-xl transform hover:-translate-y-0.5 transition-all duration-200 ${buttonClass} ${hoverClass} text-white`}
            >
              <FaPlus /> Add New
            </button>

            <button
              onClick={() => handleSearch(false)}
              className={`flex items-center gap-2 px-6 py-2 rounded-lg font-semibold shadow-lg hover:shadow-xl transform hover:-translate-y-0.5 transition-all duration-200 ${buttonClass} ${hoverClass} text-white`}
            >
              <FaSearch /> Search
            </button>

            <button
              onClick={deleteSelected}
              className="flex items-center gap-2 px-6 py-2 rounded-lg font-semibold shadow-lg hover:shadow-xl transform hover:-translate-y-0.5 transition-all duration-200 bg-red-600 hover:bg-red-700 text-white"
            >
              <FaTrash /> Delete Selected
            </button>
          </div>

          <ToastContainer
            position="top-right"
            autoClose={3000}
            hideProgressBar={false}
            closeOnClick
            pauseOnHover
            theme={isDark ? "dark" : "light"}
          />
        </div>
      </div>
    </PaymentLayoutWrapper>
  );
};

export default ContractorProjectListing;